import { Component, Output, EventEmitter, computed, inject, signal, HostListener, ElementRef } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { NotificationService } from '../../services/notification.service';
import { ProjectService } from '../../services/project.service';
import { AppNotification } from '../../models/notification.dto';
import { ActivityItemComponent } from '../activity-item/activity-item.component';
import { NotificationsFilterDropdownComponent, NotifFilter } from './components/notifications-filter-dropdown/notifications-filter-dropdown.component';

type PanelView = 'list' | 'settings';

/** View model for one global notification preference row in the in-panel settings view. */
interface NotifPrefVm {
  type: string;
  label: string;
  description: string;
  enabled: boolean;       // In-app
  emailEnabled: boolean;  // Email
}

/** A time bucket of notifications shown in the list view. */
interface NotifGroup {
  label: string;
  items: AppNotification[];
}

@Component({
  selector: 'app-notifications-panel',
  imports: [LucideAngularModule, ActivityItemComponent, NotificationsFilterDropdownComponent],
  templateUrl: './notifications-panel.component.html',
  styleUrl: './notifications-panel.component.scss',
})
export class NotificationsPanelComponent {
  readonly notificationService = inject(NotificationService);
  private readonly projectService = inject(ProjectService);
  private readonly elementRef = inject(ElementRef);

  @Output() closePanel = new EventEmitter<void>();

  /** Which view the panel shows: the notification list or the in-panel settings. */
  readonly panelView = signal<PanelView>('list');

  readonly showFilter = signal(false);
  readonly activeFilter = signal<NotifFilter>('all');

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFilter() && !this.elementRef.nativeElement.contains(event.target)) {
      this.showFilter.set(false);
    }
  }

  toggleFilter(): void {
    this.showFilter.update(v => !v);
  }

  onFilterChange(filter: NotifFilter): void {
    this.activeFilter.set(filter);
    this.showFilter.set(false);
  }

  // In-panel (global) notification settings state
  settingsPrefs: NotifPrefVm[] = [];
  prefsLoading = false;
  prefsError = false;

  /**
   * UI-only grouping + ordering of the settings rows: types about requests
   * the user is involved in vs project-wide/integration activity. Types the
   * backend adds later fall through to "Project activity".
   */
  private static readonly INVOLVED_PREF_ORDER = [
    'task.assigned',
    'request.review_requested',
    'request.updated',
    'request.deleted',
    'comment.created',
  ];
  private static readonly GENERAL_PREF_ORDER = [
    'site.published',
    'comment.mention',
    'request.created',
    'project.watch_all',
    'webflow.sync_started',
    'webflow.sync_completed',
    'webflow.sync_failed',
  ];

  get groupedSettingsPrefs(): { label: string; prefs: NotifPrefVm[] }[] {
    const orderOf = (order: string[], type: string) => {
      const i = order.indexOf(type);
      return i === -1 ? order.length : i;
    };
    const involvedOrder = NotificationsPanelComponent.INVOLVED_PREF_ORDER;
    const involved = this.settingsPrefs
      .filter((p) => involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(involvedOrder, a.type) - orderOf(involvedOrder, b.type));
    const generalOrder = NotificationsPanelComponent.GENERAL_PREF_ORDER;
    const general = this.settingsPrefs
      .filter((p) => !involvedOrder.includes(p.type))
      .sort((a, b) => orderOf(generalOrder, a.type) - orderOf(generalOrder, b.type));
    return [
      { label: "Requests you're a part of", prefs: involved },
      { label: 'General Project Activity', prefs: general },
    ].filter((g) => g.prefs.length > 0);
  }

  /** Notifications bucketed into Today / This week / Older, newest first. */
  readonly groupedNotifications = computed<NotifGroup[]>(() => {
    const filter = this.activeFilter();
    const all = [...this.notificationService.notifications()]
      .filter(n => {
        if (filter === 'read')      return n.read_at !== null;
        if (filter === 'unread')    return n.read_at === null;
        if (filter === 'mentioned') return n.type === 'comment.mention';
        return true;
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const today: AppNotification[] = [];
    const week: AppNotification[] = [];
    const older: AppNotification[] = [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekAgo = startOfToday - 6 * 24 * 60 * 60 * 1000;

    for (const n of all) {
      const t = new Date(n.created_at).getTime();
      if (t >= startOfToday) today.push(n);
      else if (t >= weekAgo) week.push(n);
      else older.push(n);
    }

    return [
      { label: 'Today', items: today },
      { label: 'This week', items: week },
      { label: 'Older', items: older },
    ].filter((g) => g.items.length > 0);
  });

  readonly totalCount = computed(() => this.notificationService.notifications().length);

  /**
   * Label at the left of the header row (Figma): the first visible group's
   * label in list view ("Today" when empty). The settings view shows a
   * "Back" pill instead. The first group skips its in-list label so it
   * isn't shown twice.
   */
  readonly headLabel = computed<string>(() => this.groupedNotifications()[0]?.label ?? 'Today');

  /** House button in the tab row → return to the regular sidebar. */
  close(): void {
    this.panelView.set('list');
    this.closePanel.emit();
  }

  onNotificationClick(notification: AppNotification): void {
    this.notificationService.navigateToDeepLink(notification);
    this.close();
  }

  onMarkRead(notification: AppNotification, event: MouseEvent): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification.id);
  }

  onMarkUnread(notification: AppNotification, event: MouseEvent): void {
    event.stopPropagation();
    this.notificationService.markAsUnread(notification.id);
  }

  onMarkAllRead(): void {
    this.notificationService.markAllRead();
  }

  // ── Settings view (In-app / Email per type) ───────────────────────
  /** Gear toggles between the list and the settings view. */
  toggleSettings(): void {
    this.showFilter.set(false);
    if (this.panelView() === 'settings') {
      this.panelView.set('list');
    } else {
      this.panelView.set('settings');
      this.loadGlobalPrefs();
    }
  }

  backToList(): void {
    this.panelView.set('list');
  }

  private get projectId(): string | null {
    return this.projectService.projectId;
  }

  private loadGlobalPrefs(): void {
    this.prefsLoading = true;
    this.prefsError = false;
    this.notificationService.getPreferences(this.projectId ?? undefined).subscribe({
      next: (prefs) => {
        this.settingsPrefs = prefs.map((p) => ({
          type: p.type,
          label: p.label,
          description: p.description,
          enabled: p.enabled,
          emailEnabled: p.send_emails,
        }));
        this.prefsLoading = false;
      },
      error: () => {
        this.prefsError = true;
        this.prefsLoading = false;
      },
    });
  }

  togglePref(pref: NotifPrefVm): void {
    const previous = pref.enabled;
    pref.enabled = !pref.enabled;
    this.savePref(pref, () => {
      pref.enabled = previous;
    });
  }

  toggleEmail(pref: NotifPrefVm): void {
    const previous = pref.emailEnabled;
    pref.emailEnabled = !pref.emailEnabled;
    this.savePref(pref, () => {
      pref.emailEnabled = previous;
    });
  }

  private savePref(pref: NotifPrefVm, revert: () => void): void {
    // Preferences are GLOBAL (apply to every project). Only the watch-all opt-in is
    // legitimately project-scoped. `|| undefined` (not ??): projectId is '' when unset,
    // and the backend treats an omitted `project` as the global row.
    const project = pref.type === 'project.watch_all' ? (this.projectId || undefined) : undefined;
    this.notificationService
      .updatePreferences([{ type: pref.type, enabled: pref.enabled, send_emails: pref.emailEnabled, project }])
      .subscribe({
        // Server-authoritative reconcile: reflect what was actually persisted.
        next: (saved) => {
          const persisted = saved?.find((s) => s.type === pref.type);
          if (persisted) {
            pref.enabled = persisted.enabled;
            if (persisted.send_emails !== undefined) {
              pref.emailEnabled = persisted.send_emails;
            }
          }
        },
        error: () => revert(),
      });
  }

  trackByNotificationId(_index: number, item: AppNotification): string {
    return item.id;
  }
}
